import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import LinksBoard, { groupShape } from './LinksBoard';
import { levelLabel, numberClash, statusLabel, warningsOf } from './linksPuzzles';
import './links.css';

// One puzzle: level, judge summary and warnings, the solved board, and
// the schedule controls (approve on a date / unschedule), regenerate (a
// dated draft), edit and delete. A dated draft approves on its own date.
// Its number is filled on approval and can be set or cleared here.
export default function LinksReviewPane({
    puzzle, puzzles, suggestedDate, busy, onApprove, onUnschedule, onSetNumber, onRegenerate, onEdit, onDelete,
}) {
    const initialDate = puzzle.date || suggestedDate;
    const [date, setDate] = useState(initialDate);
    useEffect(() => { setDate(initialDate); }, [puzzle.id, initialDate]);
    const [number, setNumber] = useState('');
    useEffect(() => { setNumber(puzzle.number ? String(puzzle.number) : ''); }, [puzzle.id, puzzle.number]);

    const scores = puzzle.scores || {};
    const warnings = warningsOf(puzzle);
    const approved = puzzle.status === 'approved';
    const clash = numberClash(puzzle, puzzles);
    const numberValue = Number(number);
    const validNumber = Number.isInteger(numberValue) && numberValue > 0;

    return (
        <div className="lk-pane">
            <div className="lk-pane-head">
                <h3>
                    Puzzle #{puzzle.id}
                    <span className={`lk-level lk-level-${puzzle.level}`}>{levelLabel(puzzle.level)}</span>
                    <span className={`lk-pill lk-pill-${puzzle.status}`}>{statusLabel(puzzle)}</span>
                </h3>
                <div className="lk-actions">
                    {approved ? (
                        <button type="button" className="lk-btn" disabled={busy} onClick={() => onUnschedule(puzzle)}>Unschedule</button>
                    ) : (
                        <>
                            <input
                                type="date"
                                aria-label="Schedule date"
                                className="lk-input lk-input-date"
                                value={date}
                                onChange={e => setDate(e.target.value)}
                            />
                            <button type="button" className="lk-btn lk-btn-primary" disabled={busy || !date} onClick={() => onApprove(puzzle, date)}>
                                Approve for date
                            </button>
                        </>
                    )}
                    {!approved && puzzle.date && (
                        <button type="button" className="lk-btn" disabled={busy} onClick={() => onRegenerate(puzzle)}>Regenerate</button>
                    )}
                    <button type="button" className="lk-btn" disabled={busy} onClick={() => onEdit(puzzle)}>Edit</button>
                    <button type="button" className="lk-btn lk-btn-danger" disabled={busy} onClick={() => onDelete(puzzle)}>Delete</button>
                </div>
            </div>
            <div className="lk-actions">
                <input
                    type="number"
                    min="1"
                    aria-label="Puzzle number"
                    placeholder="Number"
                    className="lk-input"
                    value={number}
                    onChange={e => setNumber(e.target.value)}
                />
                <button type="button" className="lk-btn" disabled={busy || !validNumber} onClick={() => onSetNumber(puzzle, numberValue)}>
                    Set number
                </button>
                <button type="button" className="lk-btn" disabled={busy || !puzzle.number} onClick={() => onSetNumber(puzzle, null)}>
                    Clear
                </button>
            </div>
            {clash && <ul className="lk-warnings"><li>#{puzzle.number} is also used by puzzle {clash.id}</li></ul>}
            {scores.red_herring !== undefined && (
                <div className="lk-scores">
                    mean red herring {scores.red_herring} · mean difficulty {scores.difficulty} · min prominence {scores.min_prominence} · min fit {scores.min_fit}
                </div>
            )}
            {warnings.length > 0 && (
                <ul className="lk-warnings">{warnings.map(w => <li key={w}>{w}</li>)}</ul>
            )}
            <LinksBoard groups={puzzle.groups} termScores={scores.terms} />
        </div>
    );
}

export const puzzleShape = PropTypes.shape({
    id: PropTypes.number,
    date: PropTypes.string,
    number: PropTypes.number,
    level: PropTypes.string,
    status: PropTypes.string,
    groups: PropTypes.arrayOf(groupShape),
    scores: PropTypes.object,
});

LinksReviewPane.propTypes = {
    puzzle: puzzleShape.isRequired,
    puzzles: PropTypes.arrayOf(puzzleShape).isRequired,
    suggestedDate: PropTypes.string.isRequired,
    busy: PropTypes.bool,
    onApprove: PropTypes.func.isRequired,
    onUnschedule: PropTypes.func.isRequired,
    onSetNumber: PropTypes.func.isRequired,
    onRegenerate: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
};

LinksReviewPane.defaultProps = { busy: false };
